import { Refresh } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";

import { RelativeTime } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { mixpanelTrack, useMixpanelContextName } from "~/lib/mixpanel-tracking";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";

import {
  useGetUnifyPortErrorsQuery,
  useGetUnifyRobotErrorsQuery
} from "~/redux/warehouse/unify.openApi";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import { UnifyErrors } from "./UnifyErrors";
import {
  getSelectedGridId,
  getSelectedWorkstationId,
  selectIsSpecificErrorViewReady,
  setSelectedGridId,
  setSelectedWorkstationId,
  setSpecificErrorViewStatus
} from "./serviceSupport.slice";

export const UnifyView = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isErrorViewHandled = useRef(false);
  const trackedPageName = useMixpanelContextName("Cube Analytics");

  const { data: autostoreGrids } = useGetAutostoreGridsQuery();
  const workstation = useAppSelector(selectThisWorkstation);
  const isSpecificErrorViewReady = useAppSelector(
    selectIsSpecificErrorViewReady
  );
  const selectedGridId = useAppSelector(getSelectedGridId);
  const selectedWorkstationId = useAppSelector(getSelectedWorkstationId);
  const { sortedWorkstations } = useGetWorkstationsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      sortedWorkstations: data?.toSorted(
        (a, b) =>
          a.autostoreGridName.localeCompare(b.autostoreGridName) ||
          a.deviceId.localeCompare(b.deviceId)
      )
    })
  });

  const gridWorkstations = sortedWorkstations?.filter(
    (workstation) => workstation.autostoreGridId === selectedGridId
  );

  const queryArg = selectedGridId
    ? {
        autostoreGridId: selectedGridId,
        workstationId: selectedWorkstationId,
        limit: null
      }
    : skipToken;

  const {
    fulfilledTimeStamp: portErrorsFulfilledTimeStamp,
    refetch: refetchPortErrors,
    isFetching: isPortErrorsFetching,
    isUninitialized: isPortErrorsUninitialized
  } = useGetUnifyPortErrorsQuery(queryArg, { pollingInterval: 60000 });

  const {
    fulfilledTimeStamp: robotErrorsFulfilledTimeStamp,
    refetch: refetchRobotErrors,
    isFetching: isRobotErrorsFetching,
    isUninitialized: isRobotErrorsUninitialized
  } = useGetUnifyRobotErrorsQuery(queryArg, { pollingInterval: 60000 });

  const isFetching = isPortErrorsFetching || isRobotErrorsFetching;
  const isInitialized =
    !isPortErrorsUninitialized || !isRobotErrorsUninitialized;
  const fulfilledTimeStamps = [
    portErrorsFulfilledTimeStamp,
    robotErrorsFulfilledTimeStamp
  ].filter((timestamp): timestamp is number => timestamp != null);
  const lastUpdatedTimeStamp = fulfilledTimeStamps.length
    ? Math.max(...fulfilledTimeStamps)
    : undefined;

  useEffect(() => {
    if (!workstation && !autostoreGrids?.length) return;

    if (isSpecificErrorViewReady) {
      if (!isErrorViewHandled.current) {
        isErrorViewHandled.current = true;
      }
      dispatch(setSpecificErrorViewStatus(false));
      return;
    }

    if (
      !workstation &&
      autostoreGrids?.length &&
      !selectedGridId &&
      !isErrorViewHandled.current
    ) {
      dispatch(setSelectedGridId(autostoreGrids[0].autostoreGridId));
      isErrorViewHandled.current = true;
    } else if (workstation && !isErrorViewHandled.current) {
      dispatch(setSelectedGridId(workstation.autostoreGridId));
      dispatch(setSelectedWorkstationId(workstation.id));
      isErrorViewHandled.current = true;
    }
  }, [
    workstation,
    autostoreGrids,
    dispatch,
    isSpecificErrorViewReady,
    selectedGridId
  ]);

  useEffect(() => {
    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "View Cube Analytics Tab"
    });
  }, [trackedPageName]);

  return (
    <Stack sx={{ m: 3, gap: 2 }}>
      <Paper
        sx={{
          p: 2
        }}
      >
        <Typography variant="h6">{t("Cube Analytics")}</Typography>
        <Typography>{t("cube analytics description")}</Typography>
        <Box
          my={2}
          display="grid"
          gridTemplateColumns="repeat(2, minmax(250px, auto)) 1fr"
          gap={2}
        >
          <FormControl>
            <FormLabel id="grid-select">{t("autostore grid")}</FormLabel>
            <Select
              labelId="grid-select"
              variant="outlined"
              value={autostoreGrids ? selectedGridId : ""}
              onChange={(e: SelectChangeEvent) => {
                mixpanelTrack({
                  trackedPageName,
                  type: "Select Option",
                  label: "Select Grid",
                  eventProperties: {
                    "Grid Name":
                      autostoreGrids?.find(
                        (grid) => grid.autostoreGridId === e.target.value
                      )?.autostoreGridName ?? ""
                  }
                });
                dispatch(setSelectedGridId(e.target.value));
                dispatch(setSelectedWorkstationId(""));
              }}
              size="medium"
              displayEmpty
            >
              {autostoreGrids?.map((grid) => (
                <MenuItem
                  key={grid.autostoreGridId}
                  value={grid.autostoreGridId}
                >
                  {`${grid.autostoreGridName}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel id="workstation-select">{t("workstation")}</FormLabel>
            <Select
              labelId="workstation-select"
              variant="outlined"
              value={gridWorkstations ? selectedWorkstationId : ""}
              onChange={(e: SelectChangeEvent) => {
                mixpanelTrack({
                  trackedPageName,
                  type: "Select Option",
                  label: "Select Workstation",
                  eventProperties: {
                    Workstation:
                      gridWorkstations?.find(
                        (workstation) => workstation.id === e.target.value
                      )?.deviceId ?? "All"
                  }
                });
                dispatch(setSelectedWorkstationId(e.target.value));
              }}
              size="medium"
              displayEmpty
            >
              <MenuItem value="">
                <em>{t("all")}</em>
              </MenuItem>
              {gridWorkstations?.map((workstation) => (
                <MenuItem key={workstation.deviceId} value={workstation.id}>
                  {`${workstation.deviceId}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack
            flexDirection="row"
            alignItems="center"
            gap={1}
            justifySelf="right"
            alignSelf="end"
          >
            {isInitialized && (
              <>
                <Typography>{t("last updated")}: </Typography>
                <RelativeTime time={lastUpdatedTimeStamp} />
                <Button
                  startIcon={<Refresh />}
                  variant="subtle"
                  disabled={isFetching}
                  onClick={async () => {
                    mixpanelTrack({
                      trackedPageName,
                      type: "Button Click",
                      label: "Refresh Port and Robot Errors"
                    });
                    await Promise.all([
                      refetchPortErrors(),
                      refetchRobotErrors()
                    ]);
                  }}
                >
                  {t("refresh")}
                </Button>
              </>
            )}
          </Stack>
        </Box>
        <UnifyErrors />
      </Paper>
    </Stack>
  );
};
